import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Clarinet } from '@gjl/site-kit/art'
import type { Project } from '@/data/portfolio'
import { ClientType } from '@/data/portfolio'

interface PortfolioCardProps {
  slug: string
  title: string
  description: string
  image: string
  imagePosition?: string
  clientTypes?: ClientType[]
  className?: string
  /**
   * Renders the vector itself in the thumbnail slot instead of `image`, for
   * the entries whose subject IS the drawing. `image` is still what the
   * og:image and any non-React consumer gets; this only changes the card.
   */
  artwork?: Project['artwork']
}

export function PortfolioCard({
  slug,
  title,
  description,
  image,
  imagePosition,
  className = '',
  artwork,
}: PortfolioCardProps) {
  return (
    <m.div
      className={`break-inside-avoid ${className}`}
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <Link to={`/portfolio/${slug}`} className="group block" aria-label={`View ${title} case study`}>
        <div className="bg-card border-border/50 overflow-hidden rounded-xl border">
          {/* Image with enforced aspect ratio, or the drawing itself */}
          <div className="aspect-[3/2] overflow-hidden">
            {artwork === 'clarinet' ? (
              /* aria-hidden: the link already names this card, and a second
                 label for the same target is noise to a screen reader. */
              <div className="clarinet-showcase h-full w-full">
                <Clarinet />
              </div>
            ) : (
              <img
                src={image}
                alt={title}
                width={600}
                height={400}
                loading="lazy"
                style={imagePosition ? { objectPosition: imagePosition } : undefined}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}
          </div>

          {/* Text below image */}
          <div className="p-4">
            <h3 className="text-foreground group-hover:text-primary text-base leading-tight font-semibold transition-colors">
              {title}
            </h3>
            <p className="text-muted-foreground mt-2 line-clamp-2 hidden text-sm leading-relaxed md:block">
              {description}
            </p>
          </div>
        </div>
      </Link>
    </m.div>
  )
}
